import React, { useState, useEffect, useRef } from 'react';
import {
  Send, Plus, MessageSquare, Calendar, BookOpen, X, ChevronLeft, ChevronRight,
  Cpu, Briefcase, Trash2, Paperclip, Camera, FileText, Image as ImageIcon,
  AlertTriangle, Loader2,
} from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiCall, apiCallStreaming, apiUpload, buildUrl, default as API_CONFIG } from '../config/api';
import {
  downscaleImage, looksLikeBitcoinTaxExport, formatBytes, formatTokens, formatCost,
} from './attachmentHelpers';
import {
  isTerminalStreamEvent, markReplyFailed, STREAM_INCOMPLETE_MESSAGE,
} from './chatStreamHelpers';

/**
 * One attachment inside a sent message. Images render as a thumbnail that
 * opens full size; everything else gets a labelled chip, since a PDF or CSV
 * has no useful preview at this size.
 */
const AttachmentPreview = ({ attachment, conversationId }) => {
  const href = conversationId
    ? buildUrl(API_CONFIG.endpoints.chatAttachmentContent(conversationId, attachment.id))
    : null;

  // The content route needs the function key, which an <img src> can't send.
  // Rendering the chip instead of a broken image is the honest fallback.
  const functionKey = localStorage.getItem('azure_function_key');
  const src = href && functionKey ? `${href}?code=${encodeURIComponent(functionKey)}` : null;

  if (attachment.kind === 'image' && src) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="attachment-thumb">
        <img src={src} alt={attachment.fileName} loading="lazy" />
      </a>
    );
  }

  const Icon = attachment.kind === 'document' ? FileText : ImageIcon;

  return (
    <span className="attachment-chip" title={`${attachment.fileName} (${formatBytes(attachment.sizeBytes)})`}>
      <Icon size={14} />
      {attachment.fileName}
      {attachment.pageCount ? ` · ${attachment.pageCount}p` : ''}
    </span>
  );
};

/**
 * What this conversation has cost, under the composer.
 *
 * Tokens and dollars are measured from the API's own usage reporting, not
 * estimated. The per-turn attachment figure is the exception and is labelled
 * as approximate -- it's the recurring cost of keeping files in context, which
 * is the number that decides whether attaching a fifth screenshot is wise.
 */
const ConversationUsageBar = ({ usage }) => {
  const totalTokens = (usage.inputTokens || 0) + (usage.outputTokens || 0);
  const cost = formatCost(usage.estimatedCostUsd);

  if (!totalTokens && !usage.attachmentCount) return null;

  return (
    <div className="conversation-usage">
      {usage.attachmentCount > 0 && (
        <span title="Attachment storage for this conversation">
          {usage.attachmentCount} file{usage.attachmentCount === 1 ? '' : 's'} ·{' '}
          {formatBytes(usage.attachmentBytes)}
        </span>
      )}

      {usage.attachmentTokensPerTurn > 0 && (
        <span title="Attachments are re-sent every turn, so this recurs">
          ~{formatTokens(usage.attachmentTokensPerTurn)} tok/turn from files
        </span>
      )}

      {totalTokens > 0 && (
        <span title="Measured from the API's usage reporting">
          {formatTokens(totalTokens)} tokens
          {cost ? ` · ${cost}` : ''}
        </span>
      )}
    </div>
  );
};

const ChatBot = () => {
  const { conversationId: routeConversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Configuration state
  const [usePhilosophy, setUsePhilosophy] = useState(true);
  const [includePortfolio, setIncludePortfolio] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [dateRangeOption, setDateRangeOption] = useState('none'); // 'none', 'last7', 'last30', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Attachment state. Files upload as soon as they're picked so the send is
  // instant and failures surface while the user is still looking at the file
  // they chose, rather than after they hit send.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState(null);
  const [csvHint, setCsvHint] = useState(false);
  const [conversationUsage, setConversationUsage] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  /**
   * Upload picked files against the current conversation.
   *
   * Images are downscaled first: a modern phone photo is 3-12MB, while the API
   * caps useful detail at ~1568px on the long edge, so full resolution costs
   * upload time on mobile data and storage forever, for pixels the model never
   * sees.
   */
  const attachFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    if (!currentConversation) {
      setAttachError('Start a conversation before attaching files.');
      return;
    }

    setUploading(true);
    setAttachError(null);

    try {
      // Flag a bitcoin.tax export before uploading -- attaching one here is
      // read-only and does NOT import it, which is worth saying out loud.
      for (const file of files) {
        if (await looksLikeBitcoinTaxExport(file)) {
          setCsvHint(true);
          break;
        }
      }

      const prepared = await Promise.all(files.map(downscaleImage));

      const result = await apiUpload(
        API_CONFIG.endpoints.chatAttachments(currentConversation.id),
        prepared
      );

      setPendingAttachments((prev) => [...prev, ...(result.attachments || [])]);
    } catch (err) {
      setAttachError(err.message || 'Failed to attach the file.');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (attachmentId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    setAttachError(null);

    try {
      await apiCall(
        API_CONFIG.endpoints.chatAttachmentById(currentConversation.id, attachmentId),
        { method: 'DELETE' }
      );
    } catch {
      // The chip is already gone from the composer; a failed cleanup leaves a
      // staged blob that the conversation delete will eventually sweep.
    }
  };

  /** Screenshots pasted straight into the composer. */
  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length === 0) return;

    e.preventDefault();
    attachFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) attachFiles(e.dataTransfer.files);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversation history and available models on mount
  useEffect(() => {
    loadConversationHistory();
    loadAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailableModels = async () => {
    try {
      const data = await apiCall(API_CONFIG.endpoints.chatModels);
      const models = data.models || [];
      setAvailableModels(models);
      // Set default model if current selection is empty or not in the list
      if (models.length > 0) {
        const modelIds = models.map(m => m.id);
        if (!selectedModel || !modelIds.includes(selectedModel)) {
          setSelectedModel(models[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load available models:', error);
      // Fallback to default model if API fails
      setAvailableModels([
        { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet', description: 'Best balance of speed and intelligence' }
      ]);
    }
  };

  const loadConversationHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await apiCall(API_CONFIG.endpoints.chatConversations);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const startNewConversation = () => {
    setShowConfig(true);
  };

  const handleConfigSubmit = async () => {
    setLoading(true);
    try {
      // Create new conversation with configuration
      const config = {
        usePhilosophy,
        includePortfolio,
        model: selectedModel,
        dateRange: dateRangeOption,
        customStartDate: dateRangeOption === 'custom' ? customStartDate : null,
        customEndDate: dateRangeOption === 'custom' ? customEndDate : null,
      };

      const response = await apiCall(API_CONFIG.endpoints.chatConversations, {
        method: 'POST',
        body: JSON.stringify(config),
      });

      const newConversation = response.conversation;
      setMessages([]);
      setShowConfig(false);
      setConversations([newConversation, ...conversations]);
      // Navigate to the new conversation's URL -- the routeConversationId
      // effect below picks it up and calls loadConversation.
      navigate(`/chat/${encodeURIComponent(newConversation.id)}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      alert('Failed to start new conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoading(true);
    try {
      const data = await apiCall(API_CONFIG.endpoints.chatConversationById(conversationId));
      setCurrentConversation(data.conversation);
      setMessages(data.messages || []);
      setConversationUsage(data.conversation?.usage || null);
      // Staged attachments belong to the conversation being left, not this one.
      setPendingAttachments([]);
      setAttachError(null);
      setCsvHint(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      alert('Failed to load conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // URL is the source of truth for which conversation is selected.
  // Re-runs whenever the :conversationId param changes (sidebar click,
  // back button, deep-link land, post-create navigation).
  useEffect(() => {
    if (!routeConversationId) {
      setCurrentConversation(null);
      setMessages([]);
      return;
    }
    if (currentConversation?.id === routeConversationId) return; // already loaded
    loadConversation(routeConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConversationId]);

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Prevent triggering the conversation load

    if (!window.confirm('Are you sure you want to delete this conversation?')) {
      return;
    }

    try {
      await apiCall(API_CONFIG.endpoints.chatConversationById(conversationId), {
        method: 'DELETE',
      });

      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // If the deleted conversation was the current one, navigate back to
      // /chat. The URL effect above clears state when the param goes away.
      if (currentConversation?.id === conversationId) {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const sendMessage = async () => {
    // An attachment on its own is a valid message -- "what do you make of
    // this?" is often carried entirely by the screenshot.
    if ((!inputMessage.trim() && pendingAttachments.length === 0) || !currentConversation) return;

    const attachmentsToSend = pendingAttachments;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      attachments: attachmentsToSend,
    };

    setMessages([...messages, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setPendingAttachments([]);
    setCsvHint(false);
    setLoading(true);

    // Create a placeholder for the streaming assistant message
    const streamingMessageId = `streaming-${Date.now()}`;
    let streamedContent = '';
    let settled = false;

    // Add empty assistant message that will be updated as content streams in
    setMessages(prev => [...prev, {
      id: streamingMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    }]);

    try {
      await apiCallStreaming(
        API_CONFIG.endpoints.chatMessagesStream(currentConversation.id),
        {
          method: 'POST',
          body: JSON.stringify({
            message: messageToSend,
            attachmentIds: attachmentsToSend.map((a) => a.id),
          }),
        },
        (event) => {
          if (isTerminalStreamEvent(event)) settled = true;

          switch (event.type) {
            case 'start':
              // Message streaming has begun
              break;

            case 'delta':
              streamedContent += event.content || '';
              setMessages(prev => prev.map(msg =>
                msg.id === streamingMessageId
                  ? { ...msg, content: streamedContent }
                  : msg
              ));
              break;

            case 'tool': {
              // Market-data lookup mid-answer. Show the in-flight one so the
              // pause before the next token reads as work, not a stall; clear
              // it once the lookup returns.
              const activity = event.toolStatus === 'running'
                ? {
                    name: event.toolName,
                    detail: event.toolDetail,
                    label: event.toolName === 'get_price_history'
                      ? `Checking price history${event.toolDetail ? ` for ${event.toolDetail}` : ''}…`
                      : `Looking up ${event.toolDetail || 'prices'}…`,
                  }
                : null;

              setMessages(prev => prev.map(msg =>
                msg.id === streamingMessageId
                  ? { ...msg, toolActivity: activity }
                  : msg
              ));
              break;
            }

            case 'done':
              // Finalize the message with the actual ID and remove streaming flag
              setMessages(prev => prev.map(msg =>
                msg.id === streamingMessageId
                  ? {
                      ...msg,
                      id: event.messageId,
                      content: event.content || streamedContent,
                      timestamp: event.timestamp || msg.timestamp,
                      isStreaming: false,
                      toolActivity: null,
                    }
                  : msg
              ));

              // Update conversation title if returned from backend
              if (event.conversationTitle) {
                const newTitle = event.conversationTitle;
                setCurrentConversation(prev =>
                  prev && prev.title !== newTitle ? { ...prev, title: newTitle } : prev
                );
                setConversations(prev => prev.map(c =>
                  c.id === currentConversation.id ? { ...c, title: newTitle } : c
                ));
              }
              break;

            case 'error':
              console.error('Streaming error:', event.error);
              setMessages(prev => markReplyFailed(prev, streamingMessageId, streamedContent, event.error));
              break;

            default:
              break;
          }
        }
      );

      // The connection closed without "done" or "error" -- don't leave the
      // reply looking like it's still being written.
      if (!settled) {
        console.error('Stream ended without a done or error event');
        setMessages(prev => markReplyFailed(prev, streamingMessageId, streamedContent, STREAM_INCOMPLETE_MESSAGE));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Update the streaming message to show error
      setMessages(prev => prev.map(msg =>
        msg.id === streamingMessageId
          ? {
              ...msg,
              content: 'Sorry, I encountered an error processing your request. Please try again.',
              isStreaming: false,
              isError: true,
            }
          : msg
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = diffInMs / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return 'Today';
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <div className="chatbot-container">
      {/* Mobile overlay when history is open */}
      {showHistory && <div className="chatbot-overlay" onClick={() => setShowHistory(false)} />}

      {/* Conversation History Sidebar */}
      <div className={`chatbot-sidebar ${showHistory ? 'sidebar-visible' : ''}`}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
            Chat History
          </h3>
          <button
            onClick={() => setShowHistory(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '0.25rem',
            }}
          >
            <ChevronLeft size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {historyLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
              Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
              No conversations yet
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => navigate(`/chat/${encodeURIComponent(conv.id)}`)}
                style={{
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: currentConversation?.id === conv.id ? 'var(--primary-light)' : 'transparent',
                  border: `1px solid ${currentConversation?.id === conv.id ? 'var(--primary-color)' : 'var(--border-color)'}`,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (currentConversation?.id !== conv.id) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentConversation?.id !== conv.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {conv.title || 'Untitled Conversation'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {formatDate(conv.createdAt)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteConversation(conv.id, e)}
                    className="conversation-delete-btn"
                    aria-label="Delete conversation"
                    title="Delete conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
          <button
            onClick={startNewConversation}
            className="btn btn-primary"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            New Chat
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chatbot-main">
        {/* Header */}
        <div className="chatbot-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flex: 1 }}>
            <button
              onClick={() => setShowHistory(true)}
              className="chatbot-toggle-btn"
              aria-label="Show chat history"
            >
              <ChevronRight size={20} />
            </button>
            <h2 className="chatbot-title">
              {currentConversation ? (currentConversation.title || 'RussAdvisor AI') : 'AI Assistant'}
            </h2>
          </div>
          {currentConversation && (
            <div className="chatbot-config-badges">
              {currentConversation.config?.usePhilosophy && (
                <span className="chatbot-badge">
                  <BookOpen size={12} />
                  <span className="badge-text">Philosophy</span>
                </span>
              )}
              {currentConversation.config?.includePortfolio && (
                <span className="chatbot-badge">
                  <Briefcase size={12} />
                  <span className="badge-text">Portfolio</span>
                </span>
              )}
              {currentConversation.config?.dateRange !== 'none' && (
                <span className="chatbot-badge">
                  <Calendar size={12} />
                  <span className="badge-text">
                    {currentConversation.config?.dateRange === 'last7' ? '7d' : currentConversation.config?.dateRange === 'last30' ? '30d' : 'Custom'}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className="chatbot-messages">
          {!currentConversation ? (
            <div className="chatbot-welcome">
              <MessageSquare size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)', fontSize: '1.125rem' }}>Welcome to RussAdvisor AI</h3>
              <p style={{ marginBottom: '1.5rem', textAlign: 'center', maxWidth: '320px', fontSize: '0.9375rem' }}>
                Start a new conversation to get investment insights powered by your philosophy.
              </p>
              <button
                onClick={startNewConversation}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Plus size={18} />
                Start New Chat
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="chatbot-welcome">
              <p>Start chatting by typing a message below</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
              >
                <div className={`message-bubble ${msg.isError ? 'error-message' : ''}`}>
                  {msg.attachments?.length > 0 && (
                    <div className="message-attachments">
                      {msg.attachments.map((a) => (
                        <AttachmentPreview
                          key={a.id}
                          attachment={a}
                          conversationId={currentConversation?.id}
                        />
                      ))}
                    </div>
                  )}
                  {msg.content}
                  {msg.isStreaming && <span className="streaming-cursor">▊</span>}
                  {msg.toolActivity && (
                    <div className="tool-activity" title={msg.toolActivity.name}>
                      <span className="tool-activity-dot" />
                      {msg.toolActivity.label}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        {currentConversation && (
          <div
            className="chatbot-input-area"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {csvHint && (
              // Attaching a bitcoin.tax export here is read-only. Without this
              // you get a fluent answer about your holdings and reasonably
              // conclude the import happened.
              <div className="attach-notice">
                <AlertTriangle size={15} />
                <span>
                  That looks like a bitcoin.tax export. Attaching it here lets you ask questions
                  about it — it does <strong>not</strong> update your portfolio. To import it, use{' '}
                  <Link to="/crypto">Crypto → Upload</Link>.
                </span>
                <button type="button" onClick={() => setCsvHint(false)} aria-label="Dismiss">
                  <X size={14} />
                </button>
              </div>
            )}

            {attachError && (
              <div className="attach-notice error">
                <AlertTriangle size={15} />
                <span>{attachError}</span>
                <button type="button" onClick={() => setAttachError(null)} aria-label="Dismiss">
                  <X size={14} />
                </button>
              </div>
            )}

            {pendingAttachments.length > 0 && (
              <div className="pending-attachments">
                {pendingAttachments.map((a) => (
                  <span key={a.id} className="pending-chip">
                    {a.kind === 'image' ? <ImageIcon size={13} /> : <FileText size={13} />}
                    <span className="pending-chip-name">{a.fileName}</span>
                    <span className="pending-chip-meta">
                      {formatBytes(a.sizeBytes)} · ~{formatTokens(a.estimatedTokens)} tok
                    </span>
                    <button type="button" onClick={() => removeAttachment(a.id)} aria-label={`Remove ${a.fileName}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="chatbot-input-container">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.txt,.md,.csv"
                style={{ display: 'none' }}
                onChange={(e) => { attachFiles(e.target.files); e.target.value = ''; }}
              />
              {/* Separate input: capture= opens the camera directly on mobile,
                  which is the wrong default when picking an existing file. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => { attachFiles(e.target.files); e.target.value = ''; }}
              />

              <button
                type="button"
                className="btn btn-secondary chatbot-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploading || !currentConversation}
                title="Attach a file"
              >
                {uploading ? <Loader2 size={18} className="spin" /> : <Paperclip size={18} />}
              </button>

              <button
                type="button"
                className="btn btn-secondary chatbot-attach-btn chatbot-camera-btn"
                onClick={() => cameraInputRef.current?.click()}
                disabled={loading || uploading || !currentConversation}
                title="Take a photo"
              >
                <Camera size={18} />
              </button>

              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onPaste={handlePaste}
                placeholder="Type a message, or paste a screenshot..."
                disabled={loading}
                className="chatbot-textarea"
                rows={1}
              />
              <button
                onClick={sendMessage}
                disabled={loading || uploading || (!inputMessage.trim() && pendingAttachments.length === 0)}
                className="btn btn-primary chatbot-send-btn"
              >
                <Send size={18} />
                <span className="send-text">{loading ? 'Sending...' : 'Send'}</span>
              </button>
            </div>

            {conversationUsage && (
              <ConversationUsageBar usage={conversationUsage} />
            )}
          </div>
        )}
      </div>

      {/* Configuration Modal */}
      {showConfig && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                Configure New Chat
              </h2>
              <button
                onClick={() => setShowConfig(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Model Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontWeight: '500',
                marginBottom: '0.75rem',
                color: 'var(--text-primary)',
              }}>
                <Cpu size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                AI Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                {availableModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} {model.description ? `- ${model.description}` : ''}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Different models offer varying levels of speed and intelligence
              </div>
            </div>

            {/* Philosophy Option */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: usePhilosophy ? 'var(--primary-light)' : 'var(--bg-primary)',
              }}>
                <input
                  type="checkbox"
                  checked={usePhilosophy}
                  onChange={(e) => setUsePhilosophy(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                    <BookOpen size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Use Latest Russ Investment Philosophy
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Include your investment philosophy to provide context-aware advice
                  </div>
                </div>
              </label>
            </div>

            {/* Portfolio Option */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: includePortfolio ? 'var(--primary-light)' : 'var(--bg-primary)',
              }}>
                <input
                  type="checkbox"
                  checked={includePortfolio}
                  onChange={(e) => setIncludePortfolio(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                    <Briefcase size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Include Current Portfolio
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Share your current holdings to get personalized portfolio advice
                  </div>
                </div>
              </label>
            </div>

            {/* Date Range Options */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontWeight: '500',
                marginBottom: '0.75rem',
                color: 'var(--text-primary)',
              }}>
                <Calendar size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Include Telegram Messages
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: dateRangeOption === 'none' ? 'var(--primary-light)' : 'var(--bg-primary)',
                }}>
                  <input
                    type="radio"
                    name="dateRange"
                    value="none"
                    checked={dateRangeOption === 'none'}
                    onChange={(e) => setDateRangeOption(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>No messages</span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: dateRangeOption === 'last7' ? 'var(--primary-light)' : 'var(--bg-primary)',
                }}>
                  <input
                    type="radio"
                    name="dateRange"
                    value="last7"
                    checked={dateRangeOption === 'last7'}
                    onChange={(e) => setDateRangeOption(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Last 7 days</span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: dateRangeOption === 'last30' ? 'var(--primary-light)' : 'var(--bg-primary)',
                }}>
                  <input
                    type="radio"
                    name="dateRange"
                    value="last30"
                    checked={dateRangeOption === 'last30'}
                    onChange={(e) => setDateRangeOption(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Last 30 days</span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: dateRangeOption === 'custom' ? 'var(--primary-light)' : 'var(--bg-primary)',
                }}>
                  <input
                    type="radio"
                    name="dateRange"
                    value="custom"
                    checked={dateRangeOption === 'custom'}
                    onChange={(e) => setDateRangeOption(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Custom date range</span>
                </label>

                {dateRangeOption === 'custom' && (
                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfig(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfigSubmit}
                disabled={loading || (dateRangeOption === 'custom' && (!customStartDate || !customEndDate))}
                className="btn btn-primary"
                style={{
                  opacity: (loading || (dateRangeOption === 'custom' && (!customStartDate || !customEndDate))) ? 0.5 : 1,
                }}
              >
                {loading ? 'Starting...' : 'Start Chat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
